import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Cpu, HardDrive, MemoryStick, Network, Play, RefreshCw, Square } from "lucide-react";
import { StatusPill } from "@ozpanel/kit";
import { Button, Eyebrow, Meter, Panel, Section, StateView, formatBytes, useAuth, useToast } from "@ozpanel/ui";

// ServiceTab — the operational baseline for the unit behind a module: is it running, for how
// long, what is it consuming, and the lifecycle controls.
//
// GET /services/{unit}/resources already returned all of this (it calls procstat.Collect) and
// nothing in the console asked for it — so "how much memory is Dovecot using" had no answer
// anywhere in the UI, on a panel whose entire job is running these services.
//
// Polls while visible: resource figures that do not move are indistinguishable from a hung
// collector, and CPU/network rates are only meaningful as a live reading.

interface Resources {
  pid: number;
  running: boolean;
  uptime_s: number;
  restarts: number;
  ncpu: number;
  cpu_percent: number;
  mem_bytes: number;
  mem_peak_bytes: number;
  mem_max_bytes: number;
  mem_percent: number;
  tasks: number;
  fds: number;
  fd_max: number;
  io_read_bytes: number;
  io_write_bytes: number;
  io_read_bps: number;
  io_write_bps: number;
  net_iface: string;
  net_rx_bps: number;
  net_tx_bps: number;
  oom_kills: number;
  mem_throttle_events: number;
  // Figures procstat could not read: zero on the wire, shown as —.
  unread?: string[];
}

interface ServiceState {
  unit: string;
  title: string;
  installed: boolean;
  state: string;
  active_state?: string;
  sub_state?: string;
  enabled?: string;
  uptime_seconds?: number;
  since?: string;
  /** Actions the API refuses for this unit, with why and what to do instead. */
  refused_actions?: Record<string, string>;
}

function dur(s?: number): string {
  if (!s) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}
const rate = (bps?: number) => (bps === undefined ? "—" : `${formatBytes(bps)}/s`);

export function ServiceTab({ unit }: { unit: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();

  const svc = useQuery({
    queryKey: ["service", unit],
    queryFn: () => client.apiFetch<ServiceState>(`/services/${unit}`),
  });
  const res = useQuery({
    queryKey: ["service-resources", unit],
    queryFn: () => client.apiFetch<Resources>(`/services/${unit}/resources`),
    // The endpoint dwells ~700ms to compute deltas; 5s keeps rates live without stacking calls.
    refetchInterval: 5000,
    retry: false,
  });

  const act = useMutation({
    mutationFn: (action: string) => client.apiFetch<unknown>(`/services/${unit}/${action}`, { method: "POST" }),
    onSuccess: (_r, action) => {
      notify({ kind: "success", title: `${unit} ${action}ed` });
      void qc.invalidateQueries({ queryKey: ["service", unit] });
      void qc.invalidateQueries({ queryKey: ["service-resources", unit] });
      void qc.invalidateQueries({ queryKey: ["module", unit] });
    },
    onError: (e, action) =>
      notify({ kind: "error", title: `Could not ${action} ${unit}`, body: e instanceof Error ? e.message : String(e) }),
  });

  const s = svc.data;
  const r = res.data;
  const unread = (k: string) => (r?.unread ?? []).includes(k);
  const running = s?.state === "running";
  const refused = s?.refused_actions ?? {};
  const allowed = (a: string) => !refused[a];

  return (
    <div className="space-y-4">
      <Section
        title="Service"
        actions={
          <div className="flex gap-2">
            {!running && allowed("start") && (
              <Button size="sm" variant="soft" disabled={act.isPending} onClick={() => act.mutate("start")}>
                <Play size={12} /> Start
              </Button>
            )}
            {allowed("reload") && (
              <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate("reload")}>
                <RefreshCw size={12} /> Reload
              </Button>
            )}
            {allowed("restart") && (
              <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate("restart")}>
                Restart
              </Button>
            )}
            {running && allowed("stop") && (
              <Button size="sm" variant="danger" disabled={act.isPending} onClick={() => act.mutate("stop")}>
                <Square size={12} /> Stop
              </Button>
            )}
          </div>
        }
      >
        <StateView loading={svc.isLoading} error={svc.error} errorContext="the service state" skeleton={<div className="h-14 animate-pulse rounded bg-surface2" />}>
          {Object.keys(refused).length > 0 && (
            <div className="mb-3 space-y-1 text-[12px] text-muted">
              {Object.entries(refused).map(([a, why]) => (
                <p key={a}>
                  <strong className="text-fg">No {a}:</strong> {why}
                </p>
              ))}
            </div>
          )}
          {s && (
            <div className="grid gap-4 sm:grid-cols-4">
              <Fact label="State" value={<StatusPill tone={running ? "ok" : s.state === "failed" ? "bad" : "neutral"}>{s.state}</StatusPill>} />
              <Fact label="Startup" value={s.enabled ?? "—"} />
              <Fact label="Uptime" value={dur(s.uptime_seconds)} />
              <Fact label="PID" value={r?.pid ? String(r.pid) : "—"} />
            </div>
          )}
        </StateView>
      </Section>

      <StateView
        loading={res.isLoading}
        error={res.isError ? res.error : undefined}
        errorContext="resource usage"
        skeleton={<div className="h-40 animate-pulse rounded-card bg-surface2" />}
      >
        {r && r.running && (
          <>
            <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
              <Usage
                icon={<Cpu size={14} />}
                label="CPU"
                value={unread("cpu") ? "—" : `${r.cpu_percent.toFixed(1)}%`}
                hint={`${r.ncpu} cores available`}
                pct={r.cpu_percent}
              />
              <Usage
                icon={<MemoryStick size={14} />}
                label="Memory"
                value={unread("memory") ? "—" : formatBytes(r.mem_bytes)}
                // A percentage is only meaningful against a LIMIT — with no cgroup cap, the
                // honest thing is the peak, not a number invented from total system memory.
                hint={r.mem_max_bytes > 0 ? `of ${formatBytes(r.mem_max_bytes)} limit` : `peak ${formatBytes(r.mem_peak_bytes)}`}
                pct={r.mem_max_bytes > 0 ? (r.mem_bytes / r.mem_max_bytes) * 100 : undefined}
              />
              <Usage
                icon={<HardDrive size={14} />}
                label="Disk I/O"
                value={`${rate(r.io_read_bps)} read`}
                hint={`${rate(r.io_write_bps)} write · ${formatBytes(r.io_write_bytes)} written total`}
              />
              {/* THE WHOLE NODE'S traffic, not this service's, and it must say so.
                  procstat reads /sys/class/net/<iface>/statistics, which is the interface counter —
                  there is no per-cgroup network accounting behind it. Rendered on a per-module
                  panel next to that module's real CPU and memory, "Network (eth0)" reads as this
                  service's traffic, and an operator sizes or blames a service on a number that
                  includes every other one. Attributing it properly needs per-cgroup accounting;
                  until then the honest fix is to stop implying. */}
              <Usage
                icon={<Network size={14} />}
                label={`Node network (${r.net_iface})`}
                value={unread("net") ? "not measured" : `${rate(r.net_rx_bps)} in`}
                hint={`${rate(r.net_tx_bps)} out · the whole node's traffic on this interface, not this service's — there is no per-service network accounting`}
              />
            </div>

            <Panel className="mt-3.5 overflow-hidden">
              <div className="border-b border-line px-4 py-2.5">
                <Eyebrow accent>Limits and faults</Eyebrow>
              </div>
              <div className="grid gap-4 p-4 sm:grid-cols-4">
                <Fact
                  label="File descriptors"
                  value={
                    <span className="tabular">
                      {r.fds}
                      {r.fd_max > 0 && <span className="text-faint"> / {r.fd_max}</span>}
                    </span>
                  }
                  meter={r.fd_max > 0 ? (r.fds / r.fd_max) * 100 : undefined}
                />
                <Fact label="Tasks" value={<span className="tabular">{r.tasks}</span>} />
                <Fact
                  label="Restarts"
                  value={
                    // NRestarts moving is the ONLY signal that separates a crash-looping unit
                    // from a healthy one — a restarting service reads "active" at every instant.
                    <span className={r.restarts > 0 ? "text-warn tabular" : "tabular"}>{r.restarts}</span>
                  }
                />
                <Fact
                  label="OOM kills"
                  value={unread("oom_kills") ? "—" : <span className={r.oom_kills > 0 ? "text-crit tabular" : "tabular"}>{r.oom_kills}</span>}
                />
              </div>
            </Panel>
          </>
        )}
        {r && !r.running && (
          <Panel className="px-4 py-6 text-center">
            <p className="text-[13.5px] text-muted">
              The unit is not running, so there is nothing to measure.
            </p>
          </Panel>
        )}
      </StateView>
    </div>
  );
}

function Fact({ label, value, meter }: { label: string; value: React.ReactNode; meter?: number }) {
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-1.5 text-[14px] font-medium">{value}</div>
      {meter !== undefined && (
        <div className="mt-2">
          <Meter pct={meter} tone={meter >= 90 ? "crit" : meter >= 75 ? "warn" : undefined} />
        </div>
      )}
    </div>
  );
}

function Usage({
  icon,
  label,
  value,
  hint,
  pct,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  pct?: number;
}) {
  return (
    <Panel className="px-4 py-3.5">
      <div className="flex items-center justify-between gap-2">
        <Eyebrow>{label}</Eyebrow>
        <span className="text-faint">{icon}</span>
      </div>
      <div className="mt-1.5 text-[20px] font-[660] tracking-[-0.015em] tabular">{value}</div>
      {pct !== undefined && (
        <div className="mt-2">
          <Meter pct={pct} tone={pct >= 90 ? "crit" : pct >= 75 ? "warn" : undefined} />
        </div>
      )}
      {hint && <div className="mt-1.5 font-mono text-[11px] text-muted">{hint}</div>}
    </Panel>
  );
}

export { Activity };
